import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon, ArrowRightIcon, ArrowLeftIcon, ClockIcon, FileCheckIcon, HandshakeIcon, MailIcon, PhoneIcon } from 'lucide-react';
import { Logo } from '../components/Logo';
export function ThankYouPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const steps = [{
    icon: <FileCheckIcon className="w-5 h-5" data-id="element-960" />,
    title: 'Underwriting Review',
    description: 'Our team reviews your deal details, contracts, and timeline to confirm fit.',
    timing: 'Within 24 hours'
  }, {
    icon: <ClockIcon className="w-5 h-5" data-id="element-961" />,
    title: 'Term Sheet Sent',
    description: 'If your deal qualifies, we send a term sheet with funding amount, fees, and timeline.',
    timing: '24–48 hours'
  }, {
    icon: <HandshakeIcon className="w-5 h-5" data-id="element-962" />,
    title: 'Coordinate with Title',
    description: 'Once you accept, we coordinate directly with your title company to wire funds for closing.',
    timing: 'Day of closing'
  }];
  return <div className="min-h-screen bg-[#f2efeb] text-[#2b2826] font-sans" data-id="element-963">
      {/* Header */}
      <header className="border-b border-[#2b2826]/10" data-id="element-964">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-5 flex items-center justify-between" data-id="element-965">
          <Link to="/" aria-label="ScaleWithLending home" data-id="element-966">
            <Logo data-id="element-967" />
          </Link>
          <Link to="/" className="text-sm text-[#2b2826]/70 hover:text-[#2b2826] transition-colors flex items-center gap-2" data-id="element-968">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-969" />
            Back to home
          </Link>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-24" data-id="element-970">
        {/* Confirmation hero */}
        <div className="text-center mb-16" data-id="element-971">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#c9a875]/15 mb-8" data-id="element-972">
            <CheckCircleIcon className="w-10 h-10 text-[#c9a875]" data-id="element-973" />
          </div>

          <p className="text-xs uppercase tracking-[0.2em] text-[#c9a875] font-medium mb-4" data-id="element-974">
            Application Received
          </p>

          <h1 className="font-serif-display text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-[#2b2826] mb-6" data-id="element-975">
            Thanks for your{' '}
            <span className="italic text-[#c9a875]" data-id="element-976">submission.</span>
          </h1>

          <p className="text-lg text-[#2b2826]/70 max-w-xl mx-auto leading-relaxed" data-id="element-977">
            Your deal is in our queue. A member of our underwriting team will
            review the details and reach out within{' '}
            <span className="text-[#2b2826] font-medium" data-id="element-978">24 hours</span> with
            next steps.
          </p>
        </div>

        {/* What happens next */}
        <section className="mb-16" data-id="element-979">
          <h2 className="font-serif-display text-2xl sm:text-3xl text-[#2b2826] mb-8 text-center" data-id="element-980">
            What happens next
          </h2>

          <ol className="space-y-4" data-id="element-981">
            {steps.map((step, idx) => <li key={idx} className="bg-white border border-[#2b2826]/10 rounded-2xl p-6 flex items-start gap-5" data-id="element-982">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#2b2826] text-[#c9a875] flex items-center justify-center" data-id="element-983">
                  {step.icon}
                </div>
                <div className="flex-1" data-id="element-984">
                  <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1" data-id="element-985">
                    <h3 className="font-medium text-[#2b2826] text-lg" data-id="element-986">
                      {idx + 1}. {step.title}
                    </h3>
                    <span className="text-xs uppercase tracking-wider text-[#c9a875] font-medium" data-id="element-987">
                      {step.timing}
                    </span>
                  </div>
                  <p className="text-[#2b2826]/70 text-sm leading-relaxed" data-id="element-988">
                    {step.description}
                  </p>
                </div>
              </li>)}
          </ol>
        </section>

        {/* Contact strip */}
        <section className="bg-[#2b2826] text-[#f2efeb] rounded-3xl p-8 sm:p-10 mb-12" data-id="element-989">
          <div className="text-center mb-6" data-id="element-990">
            <p className="text-xs uppercase tracking-[0.2em] text-[#c9a875] font-medium mb-3" data-id="element-991">
              Need to reach us
            </p>
            <h2 className="font-serif-display text-2xl sm:text-3xl" data-id="element-992">
              Questions about your deal?
            </h2>
            <p className="text-[#f2efeb]/70 mt-2 text-sm" data-id="element-993">
              Reach out directly and we'll get back to you the same day.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-994">
            <a href="mailto:support@scalewithlending.com" className="flex items-center justify-center gap-2 bg-[#c9a875] text-[#2b2826] font-medium py-3 px-6 rounded-full hover:bg-[#b08e5c] transition-colors" data-id="element-995">
              <MailIcon className="w-4 h-4" data-id="element-996" />
              support@scalewithlending.com
            </a>
            <a href="tel:+1" className="flex items-center justify-center gap-2 border border-[#f2efeb]/20 text-[#f2efeb] font-medium py-3 px-6 rounded-full hover:bg-[#1c1916] transition-colors" data-id="element-997">
              <PhoneIcon className="w-4 h-4" data-id="element-998" />
              Schedule a call
            </a>
          </div>
        </section>

        {/* Secondary CTAs */}
        <section className="text-center" data-id="element-999">
          <p className="text-sm text-[#2b2826]/60 mb-5 uppercase tracking-wider" data-id="element-1000">
            While you wait
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-1001">
            <Link to="/blog" className="inline-flex items-center justify-center gap-2 bg-[#2b2826] text-[#f2efeb] font-medium py-3 px-6 rounded-full hover:bg-[#1c1916] transition-colors" data-id="element-1002">
              Read our latest articles
              <ArrowRightIcon className="w-4 h-4" data-id="element-1003" />
            </Link>
            <Link to="/" className="inline-flex items-center justify-center gap-2 border border-[#2b2826]/20 text-[#2b2826] font-medium py-3 px-6 rounded-full hover:bg-[#2b2826]/5 transition-colors" data-id="element-1004">
              Back to home
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#2b2826]/10 mt-12" data-id="element-1005">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-6 text-center text-xs text-[#2b2826]/50" data-id="element-1006">
          © {new Date().getFullYear()} ScaleWithLending. All rights reserved.
        </div>
      </footer>
    </div>;
}